// The "Post to TikTok" step in the composer.
//
// TikTok's Direct Post API puts the obligations on the CLIENT, not on TikTok:
// the privacy level must be picked manually with no default, comment/duet/stitch
// must start off, commercial content must be disclosed, and the upload may only
// begin after express consent. None of it can be inferred later by a worker,
// which is why this step exists and why its output is persisted onto the post
// (Post.tiktokPostInfo) for the publisher to honour.
//
// Every rule lives in ./tiktokRules as a pure function so it can be tested
// without a DOM — see __tests__/tiktokRules.test.js.

import React, { useEffect, useState } from 'react';
import {
  INITIAL_STATE, blockingReason, declarationFor, disclosureLabel,
  interactionsFor, privacyOptionDisabled, brandedContentAllowed, toPostInfo, SELF_ONLY,
} from './tiktokRules';

const PRIVACY_LABELS = {
  PUBLIC_TO_EVERYONE: 'Everyone',
  MUTUAL_FOLLOW_FRIENDS: 'Friends',
  FOLLOWER_OF_CREATOR: 'Followers',
  SELF_ONLY: 'Only me',
};

export default function TikTokPostOptions({ brandId, mediaType = 'VIDEO', videoDurationSec = 0, onChange, fetchCreatorInfo }) {
  const [creator, setCreator] = useState(null);
  const [error, setError] = useState('');
  const [state, setState] = useState({ ...INITIAL_STATE, videoDurationSec });

  // 1: fetch creator_info WHEN THE PAGE RENDERS. Not cached, not from our own
  // SocialAccount row — the caps and option list are per-creator and per-day,
  // and a stale nickname would name the wrong account.
  useEffect(() => {
    let alive = true;
    if (!brandId) return undefined;
    setCreator(null); setError('');
    Promise.resolve(fetchCreatorInfo(brandId))
      .then((info) => { if (alive) setCreator(info); })
      .catch((e) => { if (alive) setError(e?.message || 'Could not reach TikTok'); });
    return () => { alive = false; };
  }, [brandId, fetchCreatorInfo]);

  useEffect(() => { setState((s) => ({ ...s, videoDurationSec })); }, [videoDurationSec]);

  const set = (patch) => setState((s) => {
    const next = { ...s, ...patch };
    // Interlock, applied in state rather than only in the UI so the invalid
    // combination cannot survive a race between two clicks.
    if (next.commercialContent && next.brandedContent && !brandedContentAllowed(next.privacyLevel)) {
      if (patch.privacyLevel === SELF_ONLY) next.brandedContent = false;
      else next.privacyLevel = '';
    }
    if (!next.commercialContent) { next.yourBrand = false; next.brandedContent = false; }
    return next;
  });

  // Report upward whenever the answer changes. The parent stores this and only
  // stamps consentedAt at submit time — hovering over the form is not consent.
  useEffect(() => {
    if (!onChange) return;
    const reason = blockingReason(state, creator);
    onChange({ ready: !reason, reason, build: () => toPostInfo(state, creator) });
  }, [state, creator, onChange]);

  if (error) return <div className="tiktok-options tiktok-options--error" role="alert">{error}</div>;
  if (!creator) return <div className="tiktok-options">Loading your TikTok account…</div>;

  const interactions = interactionsFor(mediaType);
  const reason = blockingReason(state, creator);
  const label = disclosureLabel(state);

  return (
    <div className="tiktok-options">
      {/* 1a — which account this is going to */}
      <div className="tiktok-options__creator">
        {creator.avatarUrl ? <img src={creator.avatarUrl} alt="" width={32} height={32} /> : null}
        <span>Posting to <strong>{creator.nickname}</strong>{creator.username ? ` (@${creator.username})` : ''}</span>
      </div>

      {/* 1b — hard stop, not a warning */}
      {creator.canPost === false && (
        <p className="tiktok-options__blocked" role="alert">
          TikTok says this account cannot post right now. Please try again later.
        </p>
      )}

      {/* 2b — options come from creator_info, and nothing is preselected */}
      <label className="tiktok-options__field">
        <span>Who can see this post</span>
        <select
          value={state.privacyLevel}
          onChange={(e) => set({ privacyLevel: e.target.value })}
        >
          <option value="" disabled>Select…</option>
          {(creator.privacyLevelOptions || []).map((opt) => (
            <option
              key={opt}
              value={opt}
              disabled={privacyOptionDisabled(opt, state)}
              title={privacyOptionDisabled(opt, state) ? 'Branded content visibility cannot be set to private.' : undefined}
            >
              {PRIVACY_LABELS[opt] || opt}
            </option>
          ))}
        </select>
      </label>

      {/* 2c — off by default; greyed when the creator disabled them in TikTok */}
      <fieldset className="tiktok-options__interactions">
        <legend>Allow</legend>
        {interactions.includes('comment') && (
          <label>
            <input type="checkbox" checked={state.allowComment} disabled={creator.commentDisabled}
                   onChange={(e) => set({ allowComment: e.target.checked })} />
            Comment
          </label>
        )}
        {interactions.includes('duet') && (
          <label>
            <input type="checkbox" checked={state.allowDuet} disabled={creator.duetDisabled}
                   onChange={(e) => set({ allowDuet: e.target.checked })} />
            Duet
          </label>
        )}
        {interactions.includes('stitch') && (
          <label>
            <input type="checkbox" checked={state.allowStitch} disabled={creator.stitchDisabled}
                   onChange={(e) => set({ allowStitch: e.target.checked })} />
            Stitch
          </label>
        )}
      </fieldset>

      {/* 3a — disclosure, off by default */}
      <label className="tiktok-options__disclose">
        <input type="checkbox" checked={state.commercialContent}
               onChange={(e) => set({ commercialContent: e.target.checked })} />
        Disclose content promotion
      </label>
      {state.commercialContent && (
        <div className="tiktok-options__disclose-options">
          <label>
            <input type="checkbox" checked={state.yourBrand}
                   onChange={(e) => set({ yourBrand: e.target.checked })} />
            Your brand
          </label>
          <label title={!brandedContentAllowed(state.privacyLevel) ? 'Branded content visibility cannot be set to private.' : undefined}>
            <input type="checkbox" checked={state.brandedContent}
                   disabled={!brandedContentAllowed(state.privacyLevel)}
                   onChange={(e) => set({ brandedContent: e.target.checked })} />
            Branded content
          </label>
          {label ? <p className="tiktok-options__label-note">{label}</p> : null}
        </div>
      )}

      {/* 4 — the declaration, which varies with the disclosure */}
      <p className="tiktok-options__declaration">{declarationFor(state)}</p>

      {/* 5d — set expectations about processing time */}
      <p className="tiktok-options__processing">
        After publishing, it may take a few minutes for the post to process and appear on the profile.
      </p>

      {reason ? <p className="tiktok-options__reason" role="status">{reason}</p> : null}
    </div>
  );
}
